// Shared MP4 demux/mux helpers for the WebCodecs render path.
// Proven in the render spike + GATE B proof. mp4box demuxes; mp4-muxer muxes.

import { createFile, DataStream } from 'mp4box';

// AAC AudioSpecificConfig (2 bytes, AAC-LC) — mp4-muxer needs this as the audio
// decoderConfig.description to write a valid esds when copying AAC through.
const SAMPLE_RATES = [96000, 88200, 64000, 48000, 44100, 32000, 24000, 22050,
  16000, 12000, 11025, 8000, 7350];
export function aacAudioSpecificConfig(sampleRate, channels) {
  const freqIdx = SAMPLE_RATES.indexOf(sampleRate);
  const objectType = 2; // AAC-LC
  const asc = new Uint8Array(2);
  asc[0] = (objectType << 3) | ((freqIdx >> 1) & 0x07);
  asc[1] = ((freqIdx & 0x01) << 7) | ((channels & 0x0f) << 3);
  return asc;
}

// Orientation lives in the MP4 track header's display matrix, NOT in the encoded
// samples. VideoDecoder is handed samples plus a decoder config, so it never sees
// the matrix and hands back frames in STORED orientation. A phone-shot portrait
// clip is stored landscape + a 90° matrix: every normal player rotates on playback,
// and a renderer that ignores the matrix draws it on its side. That was the bug
// reported 22 Sep 2026 — footage sideways while the caption overlay sat upright,
// because overlays are composited afterwards in the correct orientation.
// ffmpeg applies this automatically, which is why the desktop app never showed it.
//
// tkhd matrix is 9 fixed-point ints [a,b,u, c,d,v, x,y,w]; a,b,c,d are 16.16.
// Rotation is atan2(b, a). mp4box exposes it as `track.matrix` from getInfo().
const FP_16_16 = 65536;
export function rotationFromMatrix(matrix) {
  if (!matrix || matrix.length < 9) return 0;
  const a = matrix[0] / FP_16_16;
  const b = matrix[1] / FP_16_16;
  if (a === 0 && b === 0) return 0;
  let deg = Math.round((Math.atan2(b, a) * 180) / Math.PI);
  deg = ((deg % 360) + 360) % 360;
  // Snap to the four values a display matrix can legally encode; anything else
  // (skew, flip, arbitrary angle) is left alone rather than half-corrected.
  return deg === 90 || deg === 180 || deg === 270 ? deg : 0;
}

// Extract codec-private description (avcC/hvcC/…) stripped of the 8-byte box header.
function videoDescription(file, trackId) {
  const trak = file.getTrackById(trackId);
  for (const entry of trak.mdia.minf.stbl.stsd.entries) {
    const box = entry.avcC || entry.hvcC || entry.vpcC || entry.av1C;
    if (box) {
      const stream = new DataStream(undefined, 0, DataStream.BIG_ENDIAN);
      box.write(stream);
      return new Uint8Array(stream.buffer, 8);
    }
  }
  return null;
}

// Demux an MP4 ArrayBuffer → { video:{description,samples,…}, audio:{…}|null }.
// Sample timestamps are in microseconds.
export function demux(arrayBuffer) {
  return new Promise((resolve, reject) => {
    const file = createFile();
    const out = { video: null, audio: null };
    let pending = 0, ready = false;
    const maybeDone = () => { if (ready && pending === 0) resolve(out); };

    file.onError = reject;
    file.onReady = (info) => {
      const v = info.videoTracks?.[0];
      const a = info.audioTracks?.[0];
      if (!v) return reject(new Error('No video track in source'));

      out.video = {
        id: v.id, codec: v.codec, width: v.video.width, height: v.video.height,
        rotation: rotationFromMatrix(v.matrix),
        timescale: v.timescale, nbSamples: v.nb_samples,
        durationSec: v.duration / v.timescale, samples: [],
      };
      file.setExtractionOptions(v.id, null, { nbSamples: v.nb_samples });
      pending++;

      if (a) {
        out.audio = {
          id: a.id, codec: a.codec, timescale: a.timescale,
          sampleRate: a.audio.sample_rate, channels: a.audio.channel_count,
          nbSamples: a.nb_samples, samples: [],
        };
        file.setExtractionOptions(a.id, null, { nbSamples: a.nb_samples });
        pending++;
      }

      out.video.description = videoDescription(file, out.video.id);
      ready = true;
      file.start();
      maybeDone();
    };

    file.onSamples = (id, _user, samples) => {
      const track = out.video.id === id ? out.video : out.audio;
      for (const s of samples) {
        track.samples.push({
          data: s.data,
          isKey: s.is_sync,
          timestamp: (s.cts / s.timescale) * 1e6,
          duration: (s.duration / s.timescale) * 1e6,
        });
      }
      if (track.samples.length >= track.nbSamples) { pending--; maybeDone(); }
    };

    const buf = arrayBuffer;
    buf.fileStart = 0;
    file.appendBuffer(buf);
    file.flush();
  });
}

// Pick a working H.264 codec string for the target dimensions (proven in GATE B).
export async function pickH264Codec(width, height) {
  const candidates = ['avc1.640034', 'avc1.640028', 'avc1.4d0028', 'avc1.42002a', 'avc1.42001f'];
  for (const codec of candidates) {
    try {
      const { supported } = await VideoEncoder.isConfigSupported({
        codec, width, height, bitrate: 6_000_000, framerate: 30,
      });
      if (supported) return codec;
    } catch { /* try next */ }
  }
  return null;
}
